import { skipToken } from '@apollo/client/react'
import { useEffect, useState } from 'react'
import {
  GenerationModelMarkType,
  MarkType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import { executeMarkArtwork, useGetArtwork } from '../apollo/artwork'
import {
  executeMarkGenerationModel,
  useGetGenerationModel,
} from '../apollo/generation-model'

type ValidRefType = typeof RefType.Artwork | typeof RefType.Model

/**
 * Hook for like/unlike an artwork or a model
 *
 * @param targetId     - When provided an object value, no revalidation will be
 *                     invoked
 * @param targetType - When not provided, this hook does nothing
 * @param initValue
 * @returns An array with:
 *   0. State object with:
 *       - `liked`: The optimistic liked status
 *       - `count`: The optimistic liked count
 *       - `optimistic`: Whether the state is optimistic
 *   1. `setLiked`: Set or toggle the liked status
 */
export function useLike(
  targetId?: string,
  targetType?: ValidRefType,
  initValue?: { liked?: boolean; likedCount?: number },
) {
  const [prevTargetId, setPrevTargetId] = useState(targetId)
  const [toRevalidate, setToRevalidate] = useState(!initValue)

  if (targetId !== prevTargetId) {
    setPrevTargetId(targetId)
    setToRevalidate(!initValue)
  }

  const { data: artwork, refetch: refetchArtwork } = useGetArtwork(
    targetType === RefType.Artwork ? (targetId ?? skipToken) : skipToken,
    { fetchPolicy: toRevalidate ? 'cache-and-network' : 'cache-first' },
  )
  const { data: model, refetch: refetchModel } = useGetGenerationModel(
    targetType === RefType.Model ? (targetId ?? skipToken) : skipToken,
    { fetchPolicy: toRevalidate ? 'cache-and-network' : 'cache-first' },
  )

  // oxfmt-ignore
  const liked = (targetType === RefType.Artwork ? artwork?.liked : model?.liked) ?? initValue?.liked
  // oxfmt-ignore
  const likedCount = (targetType === RefType.Artwork ? artwork?.likedCount : model?.likedCount) ?? initValue?.likedCount
  const refetch = targetType === RefType.Artwork ? refetchArtwork : refetchModel
  const [optimistic, setOptimistic] = useState(false)
  const [optimisticLiked, setOptimisticLiked] = useState(Boolean(liked))
  const [optimisticLikedCount, setOptimisticLikedCount] = useState(
    likedCount ?? 0,
  )

  useEffect(() => {
    if (!optimistic) {
      setOptimisticLiked(liked ?? false)
      setOptimisticLikedCount(likedCount ?? 0)
    }
  }, [targetId, optimistic, liked, likedCount])

  async function setLiked(next = !liked) {
    if (!targetId || !targetType) return
    setToRevalidate(true)
    setOptimistic(true)
    setOptimisticLiked(val => !val)
    setOptimisticLikedCount(val => val + (optimisticLiked ? -1 : 1))
    switch (targetType) {
      case RefType.Artwork:
        await executeMarkArtwork({
          artworkId: targetId,
          type: MarkType.Like,
          target: next,
        })
        break
      case RefType.Model:
        await executeMarkGenerationModel({
          modelId: targetId,
          type: GenerationModelMarkType.Like,
          target: next,
        })
        break
    }
    await refetch()
    setOptimistic(false)
  }

  return [
    {
      liked: optimistic ? optimisticLiked : liked,
      count: optimistic ? optimisticLikedCount : likedCount,
      optimistic,
    },
    setLiked,
  ] as const
}
